import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageCircle, LogIn, UserPlus, Sparkles, AlertCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { useSeo } from "@/components/Seo";
import { getWhatsappNumbers } from "@/lib/whatsappLink";
import { isValidMobile, MOBILE_ERROR } from "@/lib/validation";

// Google Ads "Request quote" conversion — fired only after a confirmed
// successful submission (backend 2xx). Uses the existing base tag.
const QUOTE_CONVERSION_SEND_TO = "AW-18404975088/S8qmCIfLru8cEPDDlshE";
const fireQuoteConversion = () => {
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", { send_to: QUOTE_CONVERSION_SEND_TO });
  }
};

const EVENT_TYPES = ["wedding", "reception", "corporate", "birthday", "housewarming", "other"];

export default function RequestQuote() {
  useSeo({
    title: "Get a Catering Quote in Mumbai | Tezz Khana Catering",
    description: "Tell us about your event in Mumbai and your selected menu — the Tezz Khana team will confirm and share a tailored catering proposal with exact pricing.",
    path: "/request-quote",
  });
  const { t, cart, extras, eventInfo, setEventInfo, selectedPackageId, settings, clearCart, customerToken, customerInfo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomer = !!customerToken;
  const [pricing, setPricing] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmGuestOpen, setConfirmGuestOpen] = useState(false);
  const [confirmEmptyOpen, setConfirmEmptyOpen] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    mobile: "",
    whatsapp: "",
    email: "",
    event_type: eventInfo.type || "",
    event_date: "",
    event_time: "",
    venue: "",
    guest_count: eventInfo.guests || "",
    special_requirements: location.state?.smallQuantity
      ? `SMALL QUANTITY REQUEST — interested in catering below the standard package minimum${eventInfo.guests ? ` (approx. ${eventInfo.guests} guests)` : ""}.`
      : "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Estimate uses the guest count typed on THIS page (fallback: the homepage
  // planner) so the WhatsApp note / saved enquiry carry the calculated amount.
  const estGuests = Number(form.guest_count) || Number(eventInfo.guests) || 0;
  useEffect(() => {
    (async () => {
      try {
        if (selectedPackageId) {
          const { data } = await api.get("/packages");
          setSelectedPackage((data || []).find((p) => p.id === selectedPackageId) || null);
        }
        if ((cart.length + extras.length > 0 || selectedPackageId) && estGuests) {
          const { data } = await api.post("/pricing/estimate", {
            package_id: selectedPackageId || null,
            guests: estGuests,
            dish_ids: cart.map((c) => c.id),
            extras: extras.map((e) => ({
              dish_id: e.dish_id && !String(e.dish_id).startsWith("cat:") ? e.dish_id : null,
              category_id: e.category_id,
              qty: e.qty,
            })),
          });
          setPricing(data);
        }
      } catch (e) { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, extras, selectedPackageId, estGuests]);

  const selectionEmpty = cart.length === 0 && extras.length === 0 && !selectedPackageId;

  const submitStructured = async () => {
    setConfirmEmptyOpen(false);
    setSubmitting(true);
    try {
      const payload = {
        package_id: selectedPackageId || null,
        guest_count: Number(form.guest_count),
        dish_ids: cart.map((c) => c.id),
        extras: extras.map((x) => ({
          dish_id: x.dish_id && !String(x.dish_id).startsWith("cat:") ? x.dish_id : null,
          category_id: x.category_id,
          qty: x.qty,
        })),
        event_type: form.event_type || null,
        event_date: form.event_date || null,
        event_time: form.event_time || null,
        venue: form.venue || null,
        special_requirements: form.special_requirements || null,
      };
      const { data } = await api.post("/quotes", payload);
      fireQuoteConversion();
      clearCart();
      toast.success("Quote request submitted — our team will price it and send it for your approval");
      navigate(`/account/quotes/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err, t("quote.error")));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isCustomer) {
      if (!form.guest_count || Number(form.guest_count) < 1) {
        toast.error("Please enter the number of guests");
        return;
      }
      if (selectionEmpty) {
        setConfirmEmptyOpen(true);
        return;
      }
      await submitStructured();
      return;
    }
    if (!form.customer_name.trim() || !form.mobile.trim()) {
      toast.error(t("quote.required"));
      return;
    }
    if (!isValidMobile(form.mobile)) {
      toast.error(MOBILE_ERROR);
      return;
    }
    setConfirmGuestOpen(true);
  };

  const waSavedRef = useRef(false);
  const saveWhatsappEnquiry = (waNumber) => {
    if (waSavedRef.current) return;
    waSavedRef.current = true;
    const payload = {
      customer_name:
        (isCustomer && customerInfo?.name) || form.customer_name.trim() || "WhatsApp enquiry (name pending)",
      mobile: (isCustomer && customerInfo?.mobile) || form.mobile.trim() || "via WhatsApp",
      whatsapp: form.whatsapp || null,
      email: (isCustomer && customerInfo?.email) || form.email || null,
      event_type: form.event_type || eventInfo.type || null,
      event_date: form.event_date || null,
      event_time: form.event_time || null,
      venue: form.venue || null,
      guest_count: form.guest_count ? Number(form.guest_count) : eventInfo.guests ? Number(eventInfo.guests) : null,
      selected_menu: [
        ...(selectedPackage ? [{ kind: "package", id: selectedPackage.id, name: selectedPackage.name, tier: selectedPackage.tier }] : []),
        ...cart.map((c) => ({ kind: "dish", id: c.id, name: c.name, qty: c.qty })),
        ...extras.map((e) => ({ kind: "extra", dish_id: e.dish_id, category_id: e.category_id, name: e.dish_name, category: e.category_name, qty: e.qty })),
        ...(pricing ? [{ kind: "pricing", subtotal: pricing.subtotal, subtotal_partial: pricing.subtotal_partial, needs_config: pricing.needs_config }] : []),
      ],
      special_requirements: [
        form.special_requirements || null,
        `Sent via the "Send on WhatsApp" button (to ${waNumber}) — customer is messaging on WhatsApp.`,
      ].filter(Boolean).join("\n"),
    };
    api.post("/enquiries", payload).catch(() => {
      waSavedRef.current = false;
    });
  };

  const submitGuest = async () => {
    setConfirmGuestOpen(false);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
        email: form.email || null,
        selected_menu: [
          ...(selectedPackage ? [{ kind: "package", id: selectedPackage.id, name: selectedPackage.name, tier: selectedPackage.tier }] : []),
          ...cart.map((c) => ({ kind: "dish", id: c.id, name: c.name, qty: c.qty })),
          ...extras.map((e) => ({ kind: "extra", dish_id: e.dish_id, category_id: e.category_id, name: e.dish_name, category: e.category_name, qty: e.qty })),
          ...(pricing ? [{ kind: "pricing", subtotal: pricing.subtotal, subtotal_partial: pricing.subtotal_partial, needs_config: pricing.needs_config }] : []),
        ],
      };
      // strip empty optional strings so backend gets clean data
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.post("/enquiries", payload);
      fireQuoteConversion();
      setSubmitted(true);
      clearCart();
      toast.success(t("quote.success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("quote.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container-x py-16">
        <div
          data-testid="quote-success-panel"
          className="mx-auto flex max-w-lg flex-col items-center rounded-2xl border border-accent/60 bg-accent/10 p-10 text-center"
        >
          <CheckCircle2 className="h-14 w-14 text-primary" aria-hidden />
          <h1 className="mt-4 font-serif-display text-3xl font-semibold text-secondary">
            {t("quote.success")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Thank you! Our team will personally review your selection and contact you
            by phone or WhatsApp with pricing, usually within a few hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-16">
      <div className="max-w-2xl">
        <span className="kbd-eyebrow">{t("quote.title")}</span>
        <h1 className="mt-4 text-4xl font-semibold text-secondary sm:text-5xl">
          {t("quote.title")}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">{t("quote.subtitle")}</p>
      </div>

      <form
        onSubmit={submit}
        className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]"
        data-testid="quote-form"
      >
        <div className="surface-card space-y-5">
          {!isCustomer && (
            <div
              data-testid="quote-guest-banner"
              className="rounded-2xl border border-primary/30 bg-primary/5 p-4"
            >
              <div className="flex items-start gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <div className="text-sm font-semibold text-secondary">
                    Sign in to get an itemised quote
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    With an account, your menu becomes a formal quote with exact pricing, GST and
                    secure online payment — and you can track it anytime. Your selected menu is saved.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to="/account/login"
                      state={{ from: "/request-quote" }}
                      data-testid="quote-guest-login-btn"
                      className="btn-primary !py-2 !text-xs"
                    >
                      <LogIn className="h-3.5 w-3.5" /> Sign in
                    </Link>
                    <Link
                      to="/account/register"
                      state={{ from: "/request-quote" }}
                      data-testid="quote-guest-register-btn"
                      className="btn-ghost !py-2 !text-xs"
                    >
                      <UserPlus className="h-3.5 w-3.5" /> Create account
                    </Link>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Or continue below as a guest — our team will follow up on WhatsApp only.
                  </p>
                </div>
              </div>
            </div>
          )}
          {isCustomer && (
            <div
              data-testid="quote-customer-badge"
              className="flex items-center gap-2 rounded-2xl border border-accent/50 bg-accent/10 px-4 py-3 text-sm text-secondary"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
              <span>
                Submitting as <span className="font-semibold">{customerInfo?.name || "your account"}</span>
                {customerInfo?.mobile ? ` · ${customerInfo.mobile}` : ""} — you'll receive a priced quote to approve.
              </span>
            </div>
          )}
          {!isCustomer && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("quote.name")} required>
              <input
                data-testid="quote-input-name"
                value={form.customer_name}
                onChange={(e) => set("customer_name", e.target.value)}
                className="tk-input"
                required
              />
            </Field>
            <Field label={t("quote.mobile")} required>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="9812345678"
                data-testid="quote-input-mobile"
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
                className="tk-input"
                required
              />
              {form.mobile.trim() !== "" && !isValidMobile(form.mobile) && (
                <p data-testid="quote-mobile-error" className="mt-1 text-xs font-medium text-destructive">
                  {MOBILE_ERROR}
                </p>
              )}
            </Field>
            <Field label={t("quote.whatsapp")}>
              <input
                data-testid="quote-input-whatsapp"
                value={form.whatsapp}
                onChange={(e) => set("whatsapp", e.target.value)}
                className="tk-input"
              />
            </Field>
            <Field label={t("quote.email")}>
              <input
                type="email"
                data-testid="quote-input-email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="tk-input"
              />
            </Field>
          </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("quote.event_type")}>
              <select
                data-testid="quote-input-event-type"
                value={form.event_type}
                onChange={(e) => set("event_type", e.target.value)}
                className="tk-input"
              >
                <option value="">—</option>
                {EVENT_TYPES.map((et) => (
                  <option key={et} value={et}>
                    {t(`event.types.${et}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("quote.guest_count")}>
              <input
                type="number"
                min="1"
                data-testid="quote-input-guests"
                value={form.guest_count}
                onChange={(e) => {
                  set("guest_count", e.target.value);
                  setEventInfo((p) => ({ ...p, guests: e.target.value }));
                }}
                className="tk-input"
              />
              {form.guest_count !== "" && Number(form.guest_count) >= 1 && Number(form.guest_count) < 50 && (
                <div
                  data-testid="quote-guest-min-warning"
                  className="mt-2 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    Our packages start at 50 guests. For smaller quantities our team will
                    follow up with a suitable proposal — you can still continue and submit
                    this enquiry.
                  </span>
                </div>
              )}
            </Field>
            <Field label={t("quote.date")}>
              <input
                type="date"
                data-testid="quote-input-date"
                value={form.event_date}
                onChange={(e) => set("event_date", e.target.value)}
                className="tk-input"
              />
            </Field>
            <Field label={t("quote.time")}>
              <input
                type="time"
                data-testid="quote-input-time"
                value={form.event_time}
                onChange={(e) => set("event_time", e.target.value)}
                className="tk-input"
              />
            </Field>
          </div>
          <Field label={t("quote.venue")}>
            <input
              data-testid="quote-input-venue"
              value={form.venue}
              onChange={(e) => set("venue", e.target.value)}
              className="tk-input"
            />
          </Field>
          <Field label={t("quote.special")}>
            <textarea
              data-testid="quote-input-special"
              value={form.special_requirements}
              onChange={(e) => set("special_requirements", e.target.value)}
              rows={4}
              placeholder="Tell us about any special requirements for your event — e.g. Jain / no onion-garlic meals, food allergies, kids or senior-friendly meals, live counters, serving timings, buffet setup, venue access restrictions, or any other special instructions."
              className="tk-input resize-y"
            />
            <p data-testid="quote-special-note" className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              Special requirements are subject to availability and final confirmation.
            </p>
          </Field>

          <div className="flex justify-end pt-2">
            <div className="w-full">
              {isCustomer && selectionEmpty && (
                <div
                  data-testid="quote-empty-selection-warning"
                  className="mb-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    Your menu selection is empty — no package or dishes will be attached to
                    this request.{" "}
                    <button
                      type="button"
                      data-testid="quote-empty-build-menu-link"
                      onClick={() => navigate("/build-your-menu")}
                      className="font-semibold underline"
                    >
                      Build your menu
                    </button>{" "}
                    first, or submit anyway and our team will help you choose.
                  </span>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting
                    ? t("quote.submitting")
                    : isCustomer
                      ? "Submit for a priced quote"
                      : "Send as guest enquiry"}
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <aside className="surface-card h-fit" data-testid="quote-menu-summary">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Selected menu</div>
          {selectedPackage && (
            <div className="mt-3 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">{selectedPackage.tier} package</div>
              <div className="mt-0.5 font-medium text-secondary">{selectedPackage.name}</div>
            </div>
          )}
          {cart.length === 0 && extras.length === 0 && !selectedPackage ? (
            <p className="mt-3 text-sm text-muted-foreground">{t("quote.no_menu_selected")}</p>
          ) : (
            <ul className="mt-3 max-h-72 space-y-1 overflow-auto pr-1 text-sm">
              {cart.map((c) => (
                <li key={c.id} className="flex items-center justify-between rounded-lg border border-border/70 bg-background px-3 py-1.5">
                  <span className="truncate pr-3">{c.name}</span><span className="text-xs text-muted-foreground">dish</span>
                </li>
              ))}
              {extras.map((e) => (
                <li key={e.dish_id} className="flex items-center justify-between rounded-lg border border-border/70 bg-background px-3 py-1.5">
                  <span className="min-w-0 truncate pr-3">{e.dish_name} <span className="text-[10px] uppercase tracking-[0.14em] text-accent">{e.category_name}</span></span>
                  <span className="text-xs font-semibold text-secondary">×{e.qty}</span>
                </li>
              ))}
            </ul>
          )}
          {pricing && (
            <div className="mt-4 rounded-xl bg-muted/60 p-3 text-xs" data-testid="quote-price-summary">
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-[0.16em] text-muted-foreground">Subtotal</span>
                <span className="font-semibold text-secondary">
                  {pricing.subtotal === null
                    ? <span className="text-primary">Partial · ₹{Number(pricing.subtotal_partial).toLocaleString("en-IN")}</span>
                    : `₹${Number(pricing.subtotal).toLocaleString("en-IN")}`}
                </span>
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{pricing.note}</p>
            </div>
          )}
          {(() => {
            const waNumbers = getWhatsappNumbers(settings);
            if (waNumbers.length === 0) return null;
            if (!(selectedPackage || cart.length || extras.length)) return null;
            const buildText = () => encodeURIComponent(
              `Hello Tezz Khana! I would like to enquire about catering.\n` +
              (eventInfo.type ? `Event: ${eventInfo.type}\n` : "") +
              (form.guest_count || eventInfo.guests ? `Guests: ${form.guest_count || eventInfo.guests}\n` : "") +
              (selectedPackage ? `Package: ${selectedPackage.name}\n` : "") +
              (cart.length ? `Dishes: ${cart.map((c) => c.name).join(", ")}\n` : "") +
              (extras.length ? `Extras: ${extras.map((e) => `${e.dish_name} (${e.category_name}) ×${e.qty}`).join(", ")}\n` : "") +
              (pricing?.subtotal !== null && pricing?.subtotal !== undefined ? `Estimated subtotal: ₹${Number(pricing.subtotal).toLocaleString("en-IN")}\n` : "") +
              `Special Requirements: ${form.special_requirements?.trim() || "Not specified"}\n` +
              `\n(Sent from the Tezz Khana website)`
            );
            return (
              <div className="mt-3 flex flex-col gap-2">
                {waNumbers.map((n, idx) => {
                  const digits = String(n).replace(/[^\d]/g, "");
                  return (
                    <a
                      key={digits + idx}
                      data-testid={idx === 0 && waNumbers.length === 1 ? "quote-whatsapp-link" : `quote-whatsapp-link-${idx + 1}`}
                      target="_blank" rel="noopener noreferrer"
                      href={`https://wa.me/${digits}?text=${buildText()}`}
                      onClick={() => saveWhatsappEnquiry(n)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white"
                    >
                      <MessageCircle className="h-4 w-4" /> Send on WhatsApp{waNumbers.length > 1 ? ` ${idx + 1} · ${n}` : ""}
                    </a>
                  );
                })}
              </div>
            );
          })()}
        </aside>
      </form>

      <AlertDialog open={confirmGuestOpen} onOpenChange={setConfirmGuestOpen}>
        <AlertDialogContent data-testid="guest-confirm-dialog" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-xl text-secondary">
              Submitting as a guest
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
              You're submitting as a guest. Our team will personally review your selection
              and contact you by phone or WhatsApp with pricing, usually within a few hours.
              For instant online pricing and a formal quote, sign in or create a free
              account instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <button
              data-testid="guest-confirm-signin-btn"
              onClick={() => {
                setConfirmGuestOpen(false);
                navigate("/account/login", { state: { from: "/request-quote" } });
              }}
              className="btn-primary w-full !py-2.5 !text-xs sm:w-auto"
            >
              <LogIn className="h-3.5 w-3.5" /> Sign in instead
            </button>
            <button
              data-testid="guest-confirm-continue-btn"
              onClick={submitGuest}
              disabled={submitting}
              className="btn-ghost w-full !py-2.5 !text-xs sm:w-auto"
            >
              Continue as guest
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmEmptyOpen} onOpenChange={setConfirmEmptyOpen}>
        <AlertDialogContent data-testid="empty-selection-dialog" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-xl text-secondary">
              Submit without a menu selection?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
              You haven't selected a package or any dishes, so this quote request will
              reach our team without a menu attached. You can build your menu first, or
              submit anyway and we'll help you choose over WhatsApp or phone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <button
              data-testid="empty-selection-build-btn"
              onClick={() => {
                setConfirmEmptyOpen(false);
                navigate("/build-your-menu");
              }}
              className="btn-primary w-full !py-2.5 !text-xs sm:w-auto"
            >
              <Sparkles className="h-3.5 w-3.5" /> Build my menu
            </button>
            <button
              data-testid="empty-selection-submit-btn"
              onClick={submitStructured}
              disabled={submitting}
              className="btn-ghost w-full !py-2.5 !text-xs sm:w-auto"
            >
              Submit anyway
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <style>{`.tk-input { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.75rem 1rem; font-size:0.875rem; }
      .tk-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.15); }`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </span>
      {children}
    </label>
  );
}
